/**
 * @file Hive account type definitions.
 * @author Johan Nordberg <code@johan-nordberg.com>
 * @license
 * Copyright (c) 2017 Johan Nordberg. All Rights Reserved.
 *
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 *
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 *
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 *
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 *
 * You acknowledge that this software is not designed, licensed or intended for use
 * in the design, construction, operation or maintenance of any military facility.
 */

import { PublicKey } from "../crypto.js";
import { Asset } from "./asset.js";
import { AppliedOperation } from "./operation.js";

/**
 * Raw Hive authority object.
 *
 * @remarks
 * Hive authorities combine weighted account references and public-key
 * references. A transaction is authorized when collected signatures and nested
 * authorities meet `weight_threshold`.
 *
 * @example
 * ```ts
 * const authority: AuthorityType = {
 *   weight_threshold: 1,
 *   account_auths: [],
 *   key_auths: [[publicKey, 1]]
 * }
 * ```
 */
export interface AuthorityType {
  weight_threshold: number; // uint32_t
  account_auths: [string, number][]; // flat_map< account_name_type, uint16_t >
  key_auths: [string | PublicKey, number][]; // flat_map< public_key_type, uint16_t >
}

/**
 * Convenience wrapper for Hive owner, active, and posting authorities.
 *
 * @remarks
 * `Authority` can be created from a single public key for simple one-signature
 * accounts or from a full weighted authority object for multisig setups.
 *
 * @example
 * ```ts
 * const posting = Authority.from(postingPublicKey)
 * ```
 */
export class Authority implements AuthorityType {
  public weight_threshold: number;
  public account_auths: [string, number][];
  public key_auths: [string | PublicKey, number][];

  /**
   * Creates an authority from explicit threshold and auth lists.
   *
   * @param authority - Raw authority fields from Hive.
   */
  constructor({ weight_threshold, account_auths, key_auths }: AuthorityType) {
    this.weight_threshold = weight_threshold;
    this.account_auths = account_auths;
    this.key_auths = key_auths;
  }

  /**
   * Normalizes a public key or raw authority into an {@link Authority}.
   *
   * @param value - Public key string, {@link PublicKey}, existing authority, or
   * raw authority object.
   * @returns A normalized authority.
   *
   * @example
   * ```ts
   * const authority = Authority.from('STM8m5UgaFAAYQRuaNejYdS8FVLVp9Ss3K1qAVk5de6F8s3HnVbvA')
   * ```
   */
  public static from(value: string | PublicKey | AuthorityType) {
    if (value instanceof Authority) {
      return value;
    } else if (typeof value === "string" || value instanceof PublicKey) {
      return new Authority({
        account_auths: [],
        key_auths: [[value, 1]],
        weight_threshold: 1,
      });
    } else {
      return new Authority(value);
    }
  }
}

/**
 * Core Hive account object returned by condenser account lookups.
 *
 * @remarks
 * This shape includes authority keys, balances, savings balances, vesting
 * state, voting state, recovery metadata, and historical counters. Use
 * {@link ExtendedAccount} when condenser returns augmented social/history
 * fields.
 *
 * @example
 * ```ts
 * const [account] = await client.database.getAccounts(['srbde'])
 * console.log(account.balance, account.vesting_shares)
 * ```
 */
export interface Account {
  id: number; // account_id_type
  name: string; // account_name_type
  owner: Authority;
  active: Authority;
  posting: Authority;
  memo_key: string; // public_key_type
  json_metadata: string;
  posting_json_metadata: string;
  proxy: string; // account_name_type
  last_owner_update: string; // time_point_sec
  last_account_update: string; // time_point_sec
  created: string; // time_point_sec
  mined: boolean;
  owner_challenged: boolean;
  active_challenged: boolean;
  last_owner_proved: string; // time_point_sec
  last_active_proved: string; // time_point_sec
  recovery_account: string; // account_name_type
  reset_account: string; // account_name_type
  last_account_recovery: string; // time_point_sec
  comment_count: number; // uint32_t
  lifetime_vote_count: number; // uint32_t
  post_count: number; // uint32_t
  can_vote: boolean;
  voting_power: number; // uint16_t
  last_vote_time: string; // time_point_sec
  voting_manabar: {
    current_mana: string | number;
    last_update_time: number;
  };
  balance: string | Asset;
  savings_balance: string | Asset;
  hbd_balance: string | Asset;
  hbd_seconds: string; // uint128_t
  hbd_seconds_last_update: string; // time_point_sec
  hbd_last_interest_payment: string; // time_point_sec
  savings_hbd_balance: string | Asset; // asset
  savings_hbd_seconds: string; // uint128_t
  savings_hbd_seconds_last_update: string; // time_point_sec
  savings_hbd_last_interest_payment: string; // time_point_sec
  savings_withdraw_requests: number; // uint8_t
  reward_hbd_balance: string | Asset;
  reward_hive_balance: string | Asset;
  reward_vesting_balance: string | Asset;
  reward_vesting_hive: string | Asset;
  curation_rewards: number | string; // share_type
  posting_rewards: number | string; // share_type
  vesting_shares: string | Asset;
  delegated_vesting_shares: string | Asset;
  received_vesting_shares: string | Asset;
  vesting_withdraw_rate: string | Asset;
  next_vesting_withdrawal: string; // time_point_sec
  withdrawn: number | string; // share_type
  to_withdraw: number | string; // share_type
  withdraw_routes: number; // uint16_t
  proxied_vsf_votes: number[]; // vector< share_type >
  witnesses_voted_for: number; // uint16_t
  average_bandwidth: number | string; // share_type
  lifetime_bandwidth: number | string; // share_type
  last_bandwidth_update: string; // time_point_sec
  average_market_bandwidth: number | string; // share_type
  lifetime_market_bandwidth: number | string; // share_type
  last_market_bandwidth_update: string; // time_point_sec
  last_post: string; // time_point_sec
  last_root_post: string; // time_point_sec
}

/**
 * Augmented account object returned by condenser `get_accounts`.
 *
 * @remarks
 * Extended accounts add reputation, converted vesting balance, witness votes,
 * and several legacy history collections used by social applications.
 *
 * @example
 * ```ts
 * const [account] = await client.database.getAccounts(['srbde'])
 * console.log(account.reputation, account.witness_votes)
 * ```
 */
export interface ExtendedAccount extends Account {
  /**
   * Vesting shares converted to vesting HIVE for display.
   */
  vesting_balance: string | Asset;
  reputation: string | number; // share_type
  /**
   * Transfer and vesting operation history.
   */
  transfer_history: [number, AppliedOperation][]; // map<uint64_t,applied_operation>
  /**
   * Limit order, cancel, and fill history.
   */
  market_history: [number, AppliedOperation][]; // map<uint64_t,applied_operation>
  post_history: [number, AppliedOperation][]; // map<uint64_t,applied_operation>
  vote_history: [number, AppliedOperation][]; // map<uint64_t,applied_operation>
  other_history: [number, AppliedOperation][]; // map<uint64_t,applied_operation>
  witness_votes: string[]; // set<string>
  tags_usage: string[]; // vector<pair<string,uint32_t>>
  guest_bloggers: string[]; // vector<pair<account_name_type,uint32_t>>
  open_orders?: unknown[]; // optional<map<uint32_t,extended_limit_order>>
  comments?: string[]; // / permlinks for this user // optional<vector<string>>
  blog?: string[]; // / blog posts for this user // optional<vector<string>>
  feed?: string[]; // / feed posts for this user // optional<vector<string>>
  recent_replies?: string[]; // / blog posts for this user // optional<vector<string>>
  recommended?: string[]; // / posts recommened for this user // optional<vector<string>>
}
